'use client';

import { useEffect, useMemo, type ReactNode } from 'react';
import { NextIntlClientProvider, type AbstractIntlMessages } from 'next-intl';
import { I18nProvider as SharedI18nProvider, createIsolatedI18n, writeLanguageCookie } from '@workspace/i18n';
import type { HomeLocaleSnapshot } from '@/contexts/i18n/utils';

interface I18nProviderProps {
    /** 本次 SSR 准备的当前语言快照，客户端不重新加载。 */
    snapshot: HomeLocaleSnapshot;
    /** 共享同一语言的页面和公共组件。 */
    children: ReactNode;
}

export function I18nProvider({ snapshot, children }: I18nProviderProps) {
    const runtime = useMemo(() => {
        const runtime = createIsolatedI18n(snapshot.locale);
        runtime.controller.install({
            i18n: runtime.i18n,
            locale: snapshot.locale,
            hostMessages: snapshot.overrideSharedMessages ? snapshot.hostMessages : {},
            sharedMessages: snapshot.sharedMessages,
        });
        return runtime;
    }, [snapshot]);

    useEffect(() => {
        document.documentElement.lang = snapshot.locale;
        writeLanguageCookie(snapshot.locale);
    }, [snapshot.locale]);

    return (
        <NextIntlClientProvider locale={snapshot.locale} messages={snapshot.hostMessages as AbstractIntlMessages} timeZone="UTC">
            <SharedI18nProvider i18n={runtime.i18n} controller={runtime.controller}>
                {children}
            </SharedI18nProvider>
        </NextIntlClientProvider>
    );
}
